/**
 * Client-side request timeout for Apollo's GraphQL `HttpLink`.
 *
 * Apollo's `HttpLink` has no built-in request timeout, so a hung connection
 * waits forever and leaves the UI blank indefinitely. `createTimeoutFetch`
 * wraps the global `fetch` with an `AbortController` that fires after a fixed
 * budget and rejects with a distinguishable `"TimeoutError"`, so the UI can
 * surface an error + retry affordance instead of hanging (SE-5055).
 *
 * Lives in its own module (rather than inline in `ApolloClient.tsx`) so it can
 * be unit-tested in isolation and to keep `ApolloClient.tsx` under its
 * max-lines budget.
 */

/**
 * Client-side request timeout for GraphQL HTTP operations (15 seconds).
 * @remarks
 * 15s deliberately sits between the ~14s legitimate worst-case load (a cold
 * Lambda plus slow query that the user should still be allowed to wait out)
 * and the 29s backend Lambda timeout (SE-5043). Bounding the wait below the
 * backend ceiling guarantees the client decides the request's fate, so the UI
 * can show an error + retry affordance rather than a perpetual blank screen
 * (SE-5055).
 */
export const REQUEST_TIMEOUT_MS = 15000;

/**
 * Longer client timeout for the BATCHED query link. A batched POST's duration
 * is gated by the SLOWEST of its N co-batched queries (Apollo Server resolves
 * them concurrently), and under congestion the max-of-N tail routinely exceeds
 * the 15s single-op budget — which would abort the WHOLE batch and blank the
 * page (SE-5038 regression). 25s absorbs that tail while staying below the 29s
 * backend Lambda ceiling (SE-5043) so the client still decides the request's
 * fate and can show error + retry (SE-5055). Single ops and mutations keep
 * {@link REQUEST_TIMEOUT_MS}.
 */
export const BATCH_REQUEST_TIMEOUT_MS = 25000;

/**
 * Builds a `fetch` wrapper that aborts a request once `timeoutMs` elapses.
 * @remarks
 * On timeout the wrapper rejects with an `Error` whose `name` is
 * `"TimeoutError"` — deliberately distinct from the intentional `AbortError`
 * that `resetStore()` produces (SE-4940). The errorLink uses that distinction
 * to propagate timeouts (so the UI shows an error) while still swallowing
 * self-cancelled requests.
 *
 * We use a plain `Error` with `name = "TimeoutError"` rather than
 * `DOMException` or `AbortSignal.timeout()` because React Native's global
 * environment does not reliably expose those APIs.
 *
 * Any caller-provided `init.signal` is composed in, so an upstream abort (e.g.
 * `resetStore()`) still cancels the request and surfaces as a normal abort —
 * never misreported as a timeout. The timer is always cleared on settle and
 * the caller-signal listener removed, so no late abort fires and no timer or
 * listener leaks.
 * @param timeoutMs - Milliseconds before the request is aborted
 * @returns A `fetch`-compatible function enforcing the timeout
 */
export const createTimeoutFetch = (
  timeoutMs: number = REQUEST_TIMEOUT_MS
): typeof fetch => {
  const timeoutFetch: typeof fetch = (input, init) => {
    const controller = new AbortController();
    // A mutable flag is the most RN-robust way to know the abort came from our
    // timer (vs. a caller abort) without relying on `AbortSignal.reason`, which
    // is not consistently available in React Native.
    // eslint-disable-next-line functional/no-let -- timeout state must be observed in .catch
    let didTimeout = false;
    const timer = setTimeout(() => {
      didTimeout = true;
      controller.abort();
    }, timeoutMs);

    const callerSignal = init?.signal ?? undefined;
    const onCallerAbort = () => controller.abort(callerSignal?.reason);
    if (callerSignal) {
      if (callerSignal.aborted) {
        controller.abort(callerSignal.reason);
      } else {
        callerSignal.addEventListener("abort", onCallerAbort, { once: true });
      }
    }

    return fetch(input, { ...init, signal: controller.signal })
      .catch((error: unknown) => {
        if (didTimeout) {
          throw Object.assign(new Error("Request timed out"), {
            name: "TimeoutError",
          });
        }
        throw error;
      })
      .finally(() => {
        clearTimeout(timer);
        callerSignal?.removeEventListener("abort", onCallerAbort);
      });
  };

  return timeoutFetch;
};
